import { useState, useEffect, useMemo } from "react";
import { Formik, FormikHelpers } from "formik";
import { useAppDispatch } from "store";
import styles from "./Orders.module.scss";
import {
  getOrdersSchema,
  MIN_AMOUNT_ORG_1,
  getMinAmountByOrganization,
  MIN_RETURN_AMOUNT_ORG_1,
  getMinReturnAmountByOrganization,
} from "../Orders/OrdersValidation";
import Modal from "./Modal/Modal";
import { loadContractsAction } from "store/contracts/contracts.reducer";
import { IContract } from "types";
import { Loader } from "pages/Loader/Loader";
import OrderForm from "./OrderForm";
import { IUser } from "types";
import { getProfileByToken } from "utils/tokenProfile";
import { useAppSelector } from "store";
import { tokenSelector } from "store/auth/auth.selectors";
import { USER_TYPE } from "constants/enums";
import { sendForm } from "api/contracts";
import {ReactComponent as ManIcon} from 'icons/zayav_man.svg';

interface Option {
  value: string;
  label: string;
}

const orderTypeOptions: Option[] = [
  { value: "2ndfl", label: "Справка 2НДФЛ" },
  { value: "contractRefill", label: "Пополнение договора" },
  { value: "earlyReturn", label: "Заявка на досрочный возврат" },
];

const returnTypeOptions: Option[] = [
  { value: "", label: "Не выбрано" },
  { value: "fullLoan", label: "Вся сумма займа" },
  { value: "partialReturn", label: "Частичный возврат займа" },
  { value: "capitalizedInterest", label: "Капитализированные проценты" },
];
interface DescriptionMap {
  [key: string]: string;
}

const orderTypeDescriptions: DescriptionMap = orderTypeOptions.reduce(
  (acc: any, current) => {
    acc[current.value] = current.label;
    return acc;
  },
  {}
);

const returnTypeDescriptions: DescriptionMap = returnTypeOptions.reduce(
  (acc: any, current) => {
    acc[current.value] = current.label;
    return acc;
  },
  {}
);

interface FormValues {
  orderType: string;
  selectedPeriod?: string;
  selectedContract?: string;
  replenishmentAmount?: number;
  returnType?: string;
  returnAmount?: number;
  capitalizedInterestAmount?: number;
}

const OrdersPage = () => {
  const [isModalVisible, setModalVisible] = useState(false);

  const dispatch = useAppDispatch();
  const [isLoading, setIsLoading] = useState(false);
  const [contracts, setContracts] = useState<IContract[]>([]);
  const token = useAppSelector(tokenSelector);
  const [defaultSelectedContract, setDefaultSelectedContract] = useState("");

  const [selectedContractMinAmount, setSelectedContractMinAmount] =
    useState<number>(MIN_AMOUNT_ORG_1);

  const [selectedContractMinReturnAmount, setSelectedContractMinReturnAmount] =
    useState<number>(MIN_RETURN_AMOUNT_ORG_1);

  useEffect(() => {
    const loadContracts = async () => {
      setIsLoading(true);
      try {
        const response = await dispatch(
          loadContractsAction({ page: 0, pageSize: 10 })
        );
        setContracts(response.data);
        if (response.data.length > 0) {
          setDefaultSelectedContract(
            response.data[response.data.length - 1].idContract.toString()
          );
        }
      } catch (error) {
        console.error("Ошибка загрузки договоров", error);
      } finally {
        setIsLoading(false);
      }
    };

    loadContracts();
  }, [dispatch]);

  const currentYear = new Date().getFullYear();
  const years = Array.from(
    { length: currentYear - 2020 },
    (_, index) => 2021 + index
  );

  const contractOptions = [
    ...contracts.map((contract) => ({
      value: contract.idContract.toString(),
      label: contract.numContract,
      organization: contract.organization,
    })),
  ];

  const periodOptions = [
    { value: "", label: "" },
    ...years.map((year) => ({
      value: year.toString(),
      label: year.toString(),
    })),
  ];
  const profile = useMemo<IUser | null>(() => {
    return token ? getProfileByToken(token) : null;
  }, [token]);

  const initialValues: FormValues = useMemo(() => {
    const isFL = profile?.type === USER_TYPE.FL;

    return {
      orderType: isFL ? "2ndfl" : "contractRefill",
      selectedPeriod: isFL ? "" : undefined,
      selectedContract: defaultSelectedContract,
      replenishmentAmount: 0,
      returnType: "",
      returnAmount: 0,
      capitalizedInterestAmount: 0,
    };
  }, [profile, defaultSelectedContract]);

  const handleMinAmountChange = (newMinAmount: any) => {
    setSelectedContractMinAmount(newMinAmount);
  };

  useEffect(() => {
    const selectedContract = contracts.find(
      (contract) =>
        contract.idContract.toString() === initialValues.selectedContract
    );

    if (selectedContract) {
      const newMinAmount = getMinAmountByOrganization(
        selectedContract.organization.toString(),
        MIN_AMOUNT_ORG_1
      );
      const newMinReturnAmount = getMinReturnAmountByOrganization(
        selectedContract.organization.toString()
      );
      setSelectedContractMinAmount(newMinAmount);
      setSelectedContractMinReturnAmount(newMinReturnAmount);
    }
  }, [initialValues.selectedContract, contracts]);

  const selectedContractTotalAmount = useMemo(() => {
    const selectedContract = contracts.find(
      (contract) =>
        contract.idContract.toString() === initialValues.selectedContract
    );
    return selectedContract?.summa || 0;
  }, [initialValues.selectedContract, contracts]);

  const formatEmailMessage = (
    values: FormValues,
    profile: any,
    contractOptions: Option[]
  ): string => {
    let userId = profile?.idInvestor ?? "Неизвестный ID";
    let userIdentifierLabel = profile?.companyName
      ? "Название компании:"
      : "Имя пользователя:";
    let userIdentifier = profile?.companyName
      ? profile.companyName
      : `${profile?.firstName ?? "Неизвестный пользователь"} ${
          profile?.lastName ?? ""
        }`;

    const messageLines: string[] = [
      `<p><strong>${userIdentifierLabel}</strong> ${userIdentifier}</p>`,
      `<p><strong>ID пользователя:</strong> ${userId}</p>`,
      `<p><strong>Тип заявки:</strong> ${
        orderTypeDescriptions[values.orderType] ?? "Не указан"
      }</p>`,
    ];

    if (values.orderType === "earlyReturn") {
      const returnTypeDescription =
        values.returnType && returnTypeDescriptions[values.returnType]
          ? returnTypeDescriptions[values.returnType]
          : "Не указан";
      const selectedContractLabel =
        contractOptions.find(
          (option) => option.value.toString() === values.selectedContract
        )?.label ?? "Не указан";
      messageLines.push(
        `<p><strong>Выбранный договор:</strong> ${selectedContractLabel}</p>`
      );

      if (values.returnType === "fullLoan") {
        messageLines.push(
          `<p><strong>Сумма возврата:</strong> ${selectedContractTotalAmount}</p>`
        );
      } else if (
        values.returnType === "capitalizedInterest" &&
        values.capitalizedInterestAmount
      ) {
        messageLines.push(
          `<p><strong>Сумма возврата:</strong> ${values.capitalizedInterestAmount}</p>`
        );
      } else if (values.returnAmount) {
        messageLines.push(
          `<p><strong>Сумма возврата:</strong> ${values.returnAmount}</p>`
        );
      }

      messageLines.push(
        `<p><strong>Тип возврата:</strong> ${returnTypeDescription}</p>`
      );
    } else if (
      values.orderType === "contractRefill" &&
      values.replenishmentAmount
    ) {
      const selectedContractLabel =
        contractOptions.find(
          (option) => option.value.toString() === values.selectedContract
        )?.label ?? "Не указан";
      messageLines.push(
        `<p><strong>Выбранный договор:</strong> ${selectedContractLabel}</p>`,
        `<p><strong>Сумма пополнения:</strong> ${values.replenishmentAmount}</p>`
      );
    } else if (values.orderType === "2ndfl" && values.selectedPeriod) {
      messageLines.push(
        `<p><strong>Период:</strong> ${values.selectedPeriod}</p>`
      );
    }
    console.log( messageLines)
    
    return messageLines.join("\n");
  };

  const handleSubmit = (
    values: FormValues,
    formikHelpers: FormikHelpers<FormValues>
  ) => {
    const { setFieldTouched, validateForm, setSubmitting } = formikHelpers;

    Object.keys(values).forEach((field) => setFieldTouched(field, true, false));

    validateForm().then((errors) => {
      if (!Object.keys(errors).length) {
        setSubmitting(true);
        const emailBody = formatEmailMessage(values, profile, contractOptions);
        console.log("emailBody:", emailBody);
        const dataToSend = {
          ...values,
          body: emailBody,
        };
        sendForm(dataToSend)
          .then((response) => {
            console.log("Данные успешно отправлены на сервер", response);
            setModalVisible(true);
            setSubmitting(false);
          })
          .catch((error) => {
            console.error("Ошибка при отправке данных на сервер", error);
            setSubmitting(false);
          });
      } else {
        console.log("There are validation errors");
      }
    });
  };

  const handleCloseModal = () => setModalVisible(false);

  return (
    <div className={styles.wrapperContent}>
      <div className={styles.title}>Создание заявки</div>
      {isLoading ? (
        <Loader />
      ) : (
        <div className={styles.content}>
        <Formik
          initialValues={initialValues}
          validationSchema={() =>
            getOrdersSchema(
              selectedContractMinAmount,
              selectedContractMinReturnAmount,
              selectedContractTotalAmount
            )
          }
          onSubmit={handleSubmit}
        >
          {(formikProps) => (
            <OrderForm
              {...formikProps}
              contractOptions={contractOptions}
              periodOptions={periodOptions}
              orderTypeOptions={orderTypeOptions}
              returnTypeOptions={returnTypeOptions}
              profile={profile}
              onMinAmountChange={handleMinAmountChange}
              minReplenishmentAmount={selectedContractMinAmount}
              minReturnAmount={selectedContractMinReturnAmount}
            />
          )}
        </Formik>
        
        <ManIcon/>
        
      </div>
      )}
      {isModalVisible && <Modal onClose={handleCloseModal} />}
    </div>
  );
};

export default OrdersPage;
